import React from "react";
import {
  Box,
  Typography,
  Divider,
  IconButton,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { StyledTextField } from "@components/CustomInput/customInput";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootReducerType } from "@store/store";
import { genericUploadThunk } from "@modules/provisional-payment/redux/thunk/uploadThunk";
import { uploadToS3Thunk } from "@store/thunk/UploadS3Thunk";
import { productionCheck } from "@components/buttonGroupView/utils";
import { toast } from "react-toastify";
import {
  getFileROWSignedURLThunk,
  getFileSignedURLThunk,
  getDeleteSignedURLThunk,
} from "@modules/stepper/redux/thunk/documentThunk";
import { getDeleteSignedURLThunk as getDeleteSignedURLThunkROW } from "@modules/stepper-row/redux/thunk/documentThunk";
import { deleteFileOnAwsAPI } from "../../../utils/s3Upload";
import {
  fetchLegacyTransactionDetailsThunk,
  updateLegacyPaymentDetailsThunk,
} from "../redux/thunk/legacyThunk";
import { useSearchParams } from "react-router-dom";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

type LegacyPaymentDetailProps = {
  setIsStepValid?: (valid: boolean) => void;
  setSubmitHandler?: (handler: () => Promise<void> | void) => void;
};

type ReceiptMeta = {
  name: string;
  fileName: string;
  objectKey: string;
};

type PaymentSectionState = {
  transactionDate: string;
  transactionId: string;
  amountPaid: string;
  receipt: ReceiptMeta | null;
};

type BGSectionState = {
  bgDate: string;
  bgExpiryDate: string;
  bgClaimDate: string;
  bgNumber: string;
  bgAmount: string;
  bgBankName: string;
  branchAddress: string;
  receipt: ReceiptMeta | null;
};

type LicenseType = "PROCESSING" | "PROVISIONAL" | "FINAL";
type GuidelineVersion = 2013 | 2020;

const LegacyPaymentDetail: React.FC<LegacyPaymentDetailProps> = ({
  setIsStepValid,
  setSubmitHandler,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [searchParams] = useSearchParams();
  const moduleType: "row" | "access" = searchParams.get("row")
    ? "row"
    : "access";

  const {
    access_id,
    legacy_id,
    license_type,
    license_type_selection,
    transactionDetails,
    transactionDetailsLoading,
    legacy_substage_name
  } = useSelector((state: RootReducerType) => state.accessLegacy);

  const [uploadingSection, setUploadingSection] = React.useState<string | null>(
    null,
  );

  const [processingFee, setProcessingFee] = React.useState<PaymentSectionState>(
    {
      transactionDate: "",
      transactionId: "",
      amountPaid: "",
      receipt: null,
    },
  );

  const [provisionalFee, setProvisionalFee] =
    React.useState<PaymentSectionState>({
      transactionDate: "",
      transactionId: "",
      amountPaid: "",
      receipt: null,
    });

  const [finalFee, setFinalFee] = React.useState<PaymentSectionState>({
    transactionDate: "",
    transactionId: "",
    amountPaid: "",
    receipt: null,
  });

  const [bankGuarantee, setBankGuarantee] = React.useState<BGSectionState>({
    bgDate: "",
    bgExpiryDate: "",
    bgClaimDate: "",
    bgNumber: "",
    bgAmount: "",
    bgBankName: "",
    branchAddress: "",
    receipt: null,
  });
  const [guidelineVersion, setGuidelineVersion] =
    React.useState<GuidelineVersion>(2020);

  const licenseTypeOptions = [
    { label: "Processing", value: "PROCESSING" },
    { label: "Provisional", value: "PROVISIONAL" },
    { label: "Final", value: "FINAL" },
  ];

  const resolvedLicenseType =
    (licenseTypeOptions.find((option) => option.value === license_type)
      ?.value as LicenseType) || "PROCESSING";

  const sectionVisibilityByModule: Record<
    "access" | "row",
    Record<
      LicenseType,
      {
        processing: boolean;
        provisional: boolean;
        final: boolean;
        bg: boolean;
      }
    >
  > = {
    access: {
      PROCESSING: {
        processing: true,
        provisional: false,
        final: false,
        bg: false,
      },
      PROVISIONAL: {
        processing: true,
        provisional: true,
        final: false,
        bg: true,
      },
      FINAL: {
        processing: true,
        provisional: true,
        final: true,
        bg: true,
      },
    },
    row: {
      PROCESSING: {
        processing: true,
        provisional: false,
        final: false,
        bg: false,
      },
      PROVISIONAL: {
        processing: false,
        provisional: true,
        final: false,
        bg: true,
      },
      FINAL: {
        processing: false,
        provisional: false,
        final: true,
        bg: true,
      },
    },
  };

  const isRowInspectionConductedForProvisionalCase =
    moduleType === "row" &&
    resolvedLicenseType === "PROVISIONAL" &&
    String(legacy_substage_name || "").trim().toLowerCase() ===
    "inspection conducted for provisional noc";

  const isAccessInspectionConductedForProvisionalCase =
    moduleType === "access" &&
    resolvedLicenseType === "PROVISIONAL" &&
    String(legacy_substage_name || "").trim().toLowerCase() ===
    "inspection conducted for provisional noc";

  const isFinalUnderReviewInspectionConductedForFinalCase =
    moduleType === "access" &&
    resolvedLicenseType === "FINAL" &&
    license_type_selection === "FINAL_UNDER_REVIEW" &&
    String(legacy_substage_name || "").trim().toLowerCase() ===
    "inspection conducted for final noc";

  const isRowProvisionalUnderReviewInspectionConductedCase =
    moduleType === "row" &&
    resolvedLicenseType === "PROVISIONAL" && license_type_selection === "PROVISIONAL_UNDER_REVIEW" && String(legacy_substage_name || "").trim().toLowerCase() === "inspection conducted for provisional noc";

  const sectionVisibility = isRowInspectionConductedForProvisionalCase
    ? {
      processing: false,
      provisional: false,
      final: false,
      bg: false,
    }
    : isFinalUnderReviewInspectionConductedForFinalCase
      ? {
        processing: true,
        provisional: true,
        final: false,
        bg: true,
      }
      : isAccessInspectionConductedForProvisionalCase
        ? {
          processing: true,
          provisional: false,
          final: false,
          bg: false,
        }
        : sectionVisibilityByModule[moduleType][resolvedLicenseType];

  const is2013Guideline = guidelineVersion === 2013;
  const showProcessingSection = sectionVisibility.processing;
  const showProvisionalSection =
    sectionVisibility.provisional && !is2013Guideline;
  const showFinalSection = sectionVisibility.final;
  const showBankGuaranteeSection = sectionVisibility.bg && !is2013Guideline;
  const isAccessFinalWithOptionalBG = false;

  const inputSX = {
    "& .MuiOutlinedInput-root": {
      height: 36,
      fontSize: "12px",
      background: "#fff",
      outline: "none",
      borderRadius: "4px",
      "& fieldset": { borderColor: "#cbd5e1" },
      "&:hover fieldset": { borderColor: "#94a3b8" },
      "&.Mui-focused fieldset": { borderColor: "#3a1c73" },
    },
    "& .MuiOutlinedInput-input": {
      padding: "8px 10px",
    },
  };

  const isPaymentSectionValid = React.useCallback(
    (section: PaymentSectionState) => {
      return (
        !!section.transactionDate &&
        !!section.transactionId?.trim() &&
        !!section.amountPaid &&
        Number(section.amountPaid) > 0 &&
        !!section.receipt?.objectKey
      );
    },
    [],
  );

  const isBGSectionValid = React.useCallback((section: BGSectionState) => {
    return (
      !!section.bgDate &&
      !!section.bgExpiryDate &&
      !!section.bgClaimDate &&
      !!section.bgNumber?.trim() &&
      !!section.bgAmount &&
      Number(section.bgAmount) > 0 &&
      !!section.bgBankName?.trim() &&
      !!section.branchAddress?.trim() &&
      !!section.receipt?.objectKey
    );
  }, []);

  const hasAnyBGInput = React.useCallback((section: BGSectionState) => {
    return !!(
      section.bgDate ||
      section.bgExpiryDate ||
      section.bgClaimDate ||
      section.bgNumber?.trim() ||
      section.bgAmount ||
      section.bgBankName?.trim() ||
      section.branchAddress?.trim() ||
      section.receipt?.objectKey
    );
  }, []);

  // ✅ Determine if no payment is needed from transaction details
  const isNoPaymentCase = React.useMemo(() => {
    if (!Array.isArray(transactionDetails) || transactionDetails.length === 0) {
      return false;
    }
    // Check if any item has no_payment_case: true
    return transactionDetails.some(
      (item: any) => item?.no_payment_case === true,
    );
  }, [transactionDetails]);

  // ✅ When no_payment_case is true, form is auto-valid (no payment data needed)
  const isValid = isNoPaymentCase
    ? true
    : (!showProcessingSection || isPaymentSectionValid(processingFee)) &&
    (!showProvisionalSection || isPaymentSectionValid(provisionalFee)) &&
    (!showFinalSection || isPaymentSectionValid(finalFee)) &&
    (!showBankGuaranteeSection ||
      (isAccessFinalWithOptionalBG
        ? !hasAnyBGInput(bankGuarantee) || isBGSectionValid(bankGuarantee)
        : isBGSectionValid(bankGuarantee)));

  const shouldShowLastTransactionDetails = React.useMemo(() => {
    if (!Array.isArray(transactionDetails) || transactionDetails.length === 0) {
      return false;
    }

    return transactionDetails.some(
      (item: any) =>
        item?.application_type !== null && item?.application_type !== undefined,
    );
  }, [transactionDetails]);

  const buildTransactionDateISO = (dateValue: string) => {
    if (!dateValue) return null;
    const normalizedValue = dateValue.includes("T")
      ? dateValue
      : `${dateValue}T00:00:00.000Z`;
    const date = new Date(normalizedValue);
    return Number.isNaN(date.getTime()) ? null : date.toISOString();
  };

  const getBGMaxExpiryDate = (bgDate: string) => {
    if (!bgDate) return "";
    const date = new Date(`${bgDate}T00:00:00.000Z`);
    if (Number.isNaN(date.getTime())) return "";
    date.setUTCFullYear(date.getUTCFullYear() + (moduleType === "row" ? 2 : 4));
    return date.toISOString().split("T")[0];
  };

  const sanitizeAlphaNumeric = (value: string) => {
    return value.replace(/[^a-zA-Z0-9]/g, "");
  };

  const buildPaymentPayload = (
    paymentType: number,
    section: PaymentSectionState,
  ) => {
    const transactionDateISO = buildTransactionDateISO(section.transactionDate);

    if (!transactionDateISO) {
      toast.error("Please enter a valid transaction date");
      throw new Error("Invalid transaction date");
    }

    if (!section.receipt?.objectKey) {
      toast.error("Please upload receipt");
      throw new Error("Missing payment receipt");
    }

    return {
      payment_type: paymentType,
      amount_paid: Number(section.amountPaid),
      transaction_id: section.transactionId,
      transaction_date: transactionDateISO,
      payment_document: {
        fileName: section.receipt.name || section.receipt.fileName,
        objectKey: section.receipt.objectKey,
      },
    };
  };

  const handlePaymentDetailsSubmit = React.useCallback(async () => {
    try {
      if (isNoPaymentCase) {
        const payload = {
          application_id: access_id,
          application_category: moduleType === "row" ? 2 : 1,
          legacy_id,
        };
        await dispatch(updateLegacyPaymentDetailsThunk({ payload })).unwrap();
        return;
      }

      const payments: any[] = [];

      if (showProcessingSection) {
        payments.push(buildPaymentPayload(1, processingFee));
      }

      if (showProvisionalSection) {
        payments.push(buildPaymentPayload(2, provisionalFee));
      }

      if (showFinalSection) {
        payments.push(buildPaymentPayload(3, finalFee));
      }

      const payload: any = {
        application_id: access_id,
        application_category: moduleType === "row" ? 2 : 1,
        legacy_id,
        isGuidelinesCheck: guidelineVersion,
        payments,
      };

      if (showBankGuaranteeSection) {
        const shouldSubmitBG =
          !isAccessFinalWithOptionalBG || hasAnyBGInput(bankGuarantee);

        if (!shouldSubmitBG) {
          await dispatch(updateLegacyPaymentDetailsThunk({ payload })).unwrap();
          return;
        }

        const bgDateISO = buildTransactionDateISO(bankGuarantee.bgDate);
        const bgExpiryDateISO = buildTransactionDateISO(
          bankGuarantee.bgExpiryDate,
        );
        const bgClaimDateISO = buildTransactionDateISO(
          bankGuarantee.bgClaimDate,
        );

        if (!bgDateISO) {
          toast.error("Please enter a valid BG date");
          throw new Error("Invalid BG date");
        }

        if (!bgExpiryDateISO) {
          toast.error("Please enter a valid BG expiry date");
          throw new Error("Invalid BG expiry date");
        }

        if (!bgClaimDateISO) {
          toast.error("Please enter a valid BG claim date");
          throw new Error("Invalid BG claim date");
        }

        if (bankGuarantee.bgDate && bankGuarantee.bgExpiryDate) {
          const maxBGExpiryDate = getBGMaxExpiryDate(bankGuarantee.bgDate);

          if (bankGuarantee.bgExpiryDate <= bankGuarantee.bgDate) {
            toast.error(
              "BG Expiry Date must be later than BG Date of Submission",
            );
            throw new Error("Invalid BG expiry minimum date");
          }

          if (maxBGExpiryDate && bankGuarantee.bgExpiryDate > maxBGExpiryDate) {
            toast.error(
              `BG Expiry Date cannot be more than ${moduleType === "row" ? 2 : 4} years from BG Date of Submission`,
            );
            throw new Error("Invalid BG expiry maximum date");
          }

          if (bankGuarantee.bgClaimDate < bankGuarantee.bgDate) {
            toast.error(
              "BG Claim Date must be on or after BG Date of Submission",
            );
            throw new Error("Invalid BG claim minimum date");
          }

          if (maxBGExpiryDate && bankGuarantee.bgClaimDate > maxBGExpiryDate) {
            toast.error(
              `BG Claim Date cannot be more than ${moduleType === "row" ? 2 : 4} years from BG Date of Submission`,
            );
            throw new Error("Invalid BG claim maximum date");
          }
        }

        if (!bankGuarantee.receipt?.objectKey) {
          toast.error("Please upload BG receipt");
          throw new Error("Missing BG receipt");
        }

        payload.bg_details = {
          bg_number: bankGuarantee.bgNumber,
          bg_amount: Number(bankGuarantee.bgAmount),
          bg_date: bgDateISO,
          bg_expiry_date: bgExpiryDateISO,
          bg_claim_date: bgClaimDateISO,
          bg_bank_name: bankGuarantee.bgBankName,
          branch_address: bankGuarantee.branchAddress,
          bg_document: {
            fileName:
              bankGuarantee.receipt.name || bankGuarantee.receipt.fileName,
            objectKey: bankGuarantee.receipt.objectKey,
          },
        };
      }

      await dispatch(updateLegacyPaymentDetailsThunk({ payload })).unwrap();

    } catch (error: any) {
      // ✅ Check if the error is from the API response
      if (error?.message) {
        toast.error(error.message);
      }
      throw error;
    }
  }, [
    access_id,
    legacy_id,
    moduleType,
    guidelineVersion,
    processingFee,
    showProcessingSection,
    showProvisionalSection,
    provisionalFee,
    showFinalSection,
    finalFee,
    showBankGuaranteeSection,
    bankGuarantee,
    isNoPaymentCase,
    dispatch,
  ]);

  // Fetch transaction details when access_id is available
  React.useEffect(() => {
    if (access_id) {
      dispatch(fetchLegacyTransactionDetailsThunk(access_id));
    }
  }, [access_id, dispatch]);

  React.useEffect(() => {
    setIsStepValid?.(isValid);
  }, [isValid, setIsStepValid]);

  React.useEffect(() => {
    if (!setSubmitHandler) return;
    setSubmitHandler(handlePaymentDetailsSubmit);
  }, [setSubmitHandler, handlePaymentDetailsSubmit]);

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
    sectionKey: string,
    onUploaded: (receipt: ReceiptMeta) => void,
  ) => {
    const fileInput = event.target;
    const file = fileInput.files?.[0];
    if (!file) return;

    // Reset so same file can be re-selected
    fileInput.value = "";

    if (file.size > 21 * 1024 * 1024) {
      toast.error("File size must be less than 20 MB");
      return;
    }

    setUploadingSection(sectionKey);
    try {
      // Step 1 – get signed URL / upload URL
      const fileUploadURL = await dispatch(
        genericUploadThunk({ file, moduleType }),
      );

      if (!fileUploadURL?.payload?.success) {
        const errorMsg =
          fileUploadURL?.payload?.response?.data?.details ||
          fileUploadURL?.payload?.details;
        toast.error(
          Array.isArray(errorMsg)
            ? errorMsg[0]
            : typeof errorMsg === "string"
              ? errorMsg
              : "Something went wrong!",
        );
        return;
      }

      // Step 2 – push to S3 (non-prod only)
      if (!productionCheck) {
        const uploadSuccess = await dispatch(
          uploadToS3Thunk({
            signedUrl: fileUploadURL.payload.data.signedUrl,
            file,
            contentType: file.type,
          }),
        ).unwrap();

        if (!uploadSuccess) {
          throw new Error("File upload to S3 failed");
        }
      }

      onUploaded({
        name: file.name,
        objectKey: fileUploadURL.payload.data.objectKey,
        fileName: fileUploadURL.payload.data.fileName,
      });

      toast.success("Receipt uploaded successfully");
    } catch (error) {
      console.error(error);
      toast.error("Upload failed. Please try again.");
    } finally {
      setUploadingSection(null);
    }
  };

  const handleViewFile = async (receipt: ReceiptMeta | null) => {
    if (!receipt?.objectKey) return;
    try {
      const signedUrlThunk =
        moduleType === "row" ? getFileROWSignedURLThunk : getFileSignedURLThunk;
      const result = await dispatch(
        signedUrlThunk({ document_name: receipt.fileName }),
      );
      const signedUrl = result?.payload?.data?.signedUrl;
      if (signedUrl) {
        window.open(signedUrl, "_blank");
      } else {
        toast.error("Unable to open file");
      }
    } catch {
      toast.error("Unable to open file");
    }
  };

  const handleRemoveFile = async (
    receipt: ReceiptMeta | null,
    onCleared: () => void,
  ) => {
    if (!receipt?.fileName) {
      onCleared();
      return;
    }

    try {
      const deleteThunk =
        moduleType === "row"
          ? getDeleteSignedURLThunkROW
          : getDeleteSignedURLThunk;
      const deleteRes = await dispatch(deleteThunk(receipt.fileName as any));
      const signedUrl = deleteRes.payload?.data?.signedUrl;
      if (signedUrl) {
        await deleteFileOnAwsAPI(signedUrl);
      }
    } catch (error) {
      console.error("Delete error:", error);
    }
    onCleared();
  };

  const renderReceiptUpload = (
    id: string,
    receipt: ReceiptMeta | null,
    sectionKey: string,
    onUploaded: (receiptMeta: ReceiptMeta) => void,
    onClear: () => void,
    optional?: boolean,
  ) => {
    return (
      <Box mt={2}>
        {/* <Typography fontSize={12} fontWeight={600} mb={1}>
          Upload Receipt
          {optional ? " (Optional)" : <span className="required">*</span>}
        </Typography>
        <Divider sx={{ mb: 1.5 }} /> */}

        <input
          type="file"
          id={id}
          style={{ display: "none" }}
          accept=".pdf"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            handleFileUpload(event, sectionKey, onUploaded)
          }
        />

        <Box
          sx={{
            background: "#eef2f7",
            borderRadius: "6px",
            px: 2,
            py: 1,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Box>
            <Typography fontSize={11}>
              Upload Receipt{optional ? " (Optional)" : "*"}
            </Typography>
            <Typography fontSize={10} color="#9ca3af">
              (Supported Format PDF only)
            </Typography>
          </Box>

          {uploadingSection === sectionKey ? (
            <CircularProgress size={20} sx={{ color: "#3A1C73" }} />
          ) : receipt?.objectKey ? (
            <Box display="flex" alignItems="center" gap={1}>
              <Typography
                sx={{
                  fontSize: 11,
                  color: "#757575",
                  cursor: "pointer",
                  textDecoration: "underline",
                  "&:hover": { color: "#3A1C73" },
                }}
                onClick={() => handleViewFile(receipt)}
                title={receipt.name}
              >
                {receipt.name && receipt.name.length > 20
                  ? `${receipt.name.slice(0, 20)}...`
                  : receipt.name}
              </Typography>

              <IconButton
                onClick={() => handleViewFile(receipt)}
                sx={{
                  background: "#FFFFFF",
                  borderRadius: "8px",
                  border: "1px solid #D0D5DD",
                  padding: "6px 8px",
                  "&:hover": { backgroundColor: "#E7E4F8" },
                }}
              >
                <RemoveRedEyeOutlinedIcon fontSize="small" />
              </IconButton>

              <IconButton
                onClick={() => handleRemoveFile(receipt, onClear)}
                sx={{
                  background: "#FFFFFF",
                  borderRadius: "8px",
                  border: "1px solid #D0D5DD",
                  padding: "6px 8px",
                  "&:hover": { backgroundColor: "#E7E4F8" },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          ) : (
            <Typography
              component="label"
              htmlFor={id}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                fontSize: 11,
                color: "#3a1c73",
                cursor: "pointer",
              }}
            >
              <UploadFileIcon fontSize="small" />
              Upload File
            </Typography>
          )}
        </Box>
      </Box>
    );
  };

  const bgDateDayjs = bankGuarantee.bgDate ? dayjs(bankGuarantee.bgDate) : null;
  const bgExpiryDateDayjs = bankGuarantee.bgExpiryDate
    ? dayjs(bankGuarantee.bgExpiryDate)
    : null;
  const bgClaimDateDayjs = bankGuarantee.bgClaimDate
    ? dayjs(bankGuarantee.bgClaimDate)
    : null;
  const bgMaxExpiryDayjs = bgDateDayjs
    ? bgDateDayjs.add(moduleType === "row" ? 2 : 4, "year")
    : null;

  React.useEffect(() => {
    if (!showFinalSection) {
      setFinalFee({
        transactionDate: "",
        transactionId: "",
        amountPaid: "",
        receipt: null,
      });
    }
  }, [showFinalSection]);

  return (
    <Box className="amenities-container" component="div">
      <h3 className="stepper-hd w-full body-l flex items-center gap-3 mb-2">
        Payment Details to be verified by Authority
        {isNoPaymentCase && (
          <Typography
            sx={{ fontSize: "12px", color: "#1083b9", fontWeight: 500, bgcolor: "#eef2f7", px: 1.5, py: 0.5, borderRadius: "1rem" }}
          >
            No Payment Required
          </Typography>
        )}
      </h3>

      <Box className="form-wrapper" sx={{ paddingTop: 0 }}>

        {!isNoPaymentCase && (
          <FormControl sx={{ my: 1.5 }}>
            <Typography sx={{ fontSize: "16px", fontWeight: 600, my: 0.5 }}>
              Select the applicable MoRTH Guidelines under which this application is being submitted:
            </Typography>
            <RadioGroup
              row
              name="guideline-version"
              value={guidelineVersion}
              onChange={(event) =>
                setGuidelineVersion(Number(event.target.value) as GuidelineVersion)
              }
            >
              <FormControlLabel
                value={2013}
                control={<Radio size="medium" sx={{
                  color: "#3A1C73",
                  "&.Mui-checked": { color: "#3A1C73" },
                }} />}
                label="Guidelines dated 24.07.2013"
                sx={{
                  "& .MuiFormControlLabel-label": {
                    fontSize: "14px",       // 👈 label size
                    color: "#111827",
                    fontWeight: 400,
                  },
                }}
              />
              <FormControlLabel
                value={2020}
                control={<Radio size="medium" sx={{
                  color: "#3A1C73",
                  "&.Mui-checked": { color: "#3A1C73" },
                }} />}
                label="Guidelines dated 26.06.2020"
                sx={{
                  "& .MuiFormControlLabel-label": {
                    fontSize: "14px",       // 👈 label size
                    color: "#111827",
                    fontWeight: 400,
                  },
                }}
              />
            </RadioGroup>
          </FormControl>
        )}

        {shouldShowLastTransactionDetails && (
          <Box
            sx={{
              mb: 3,
              background: "#F5F8FC",
              borderRadius: "6px",
              p: 1.5,
              mt: 2.2,
            }}
          >
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 700,
                color: "#111827",
                mb: 1.5,
              }}
            >
              Last Transaction Details
            </Typography>

            {transactionDetailsLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={32} />
              </Box>
            ) : transactionDetails && transactionDetails.length > 0 ? (
              transactionDetails.map((item: any, index: number) => (
                <Box key={item.trans_id ?? item.order_no ?? index}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
                      rowGap: 1,
                      columnGap: 2,
                      pb: 1.25,
                    }}
                  >
                    <Box>
                      <Typography sx={{ fontSize: "12px", color: "#6b7280" }}>
                        Transaction ID
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "12px",
                          color: "#111827",
                          fontWeight: 600,
                        }}
                      >
                        {item.order_no || "-"}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography sx={{ fontSize: "12px", color: "#6b7280" }}>
                        Transaction Date
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "12px",
                          color: "#111827",
                          fontWeight: 600,
                        }}
                      >
                        {item.tdate
                          ? new Date(item.tdate).toLocaleDateString("en-IN", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })
                          : "-"}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography sx={{ fontSize: "12px", color: "#6b7280" }}>
                        Payment Amount
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "12px",
                          color: "#111827",
                          fontWeight: 600,
                        }}
                      >
                        {item.amt
                          ? `₹ ${Number(item.amt).toLocaleString("en-IN")}`
                          : "-"}
                      </Typography>
                    </Box>
                  </Box>

                  {index < transactionDetails.length - 1 && (
                    <Divider sx={{ mb: 1.25 }} />
                  )}
                </Box>
              ))
            ) : (
              <Typography sx={{ fontSize: "12px", color: "#6b7280", py: 2 }}>
                No transaction details available
              </Typography>
            )}
          </Box>
        )}

        {/* {!isNoPaymentCase && (
          <Typography
            sx={{ fontSize: "17px", fontWeight: 700, color: "#111827", my: 2 }}
          >
            Enter payment details to be verified by authority
          </Typography>
        )} */}

        {(isNoPaymentCase || isRowProvisionalUnderReviewInspectionConductedCase) && (
          <Box
            sx={{
              background: "#d3ecf9",
              borderRadius: "6px",
              p: 2.5,
              my: 2,
              border: "1px solid #28a1e7",
            }}
          >
            <Typography
              sx={{
                fontSize: "14px",
                color: "#000000",
                fontWeight: 500,
                mb: 1.5,
                lineHeight: 1.6,
              }}
            >
              No payment is required for this application. Applications under
              the following categories are exempt from payment of the prescribed
              NOC fee:
            </Typography>

            {isNoPaymentCase && (
              <Box component="ul" sx={{ pl: 2.5, mb: 1.5 }}>
                <Typography
                  component="li"
                  sx={{
                    fontSize: "14px",
                    color: "#000000",
                    fontWeight: 500,
                    mb: 0.8,
                    lineHeight: 1.6,
                  }}
                >
                  Access – Private Properties/ Other Establishments (Other than
                  fuel stations) -{" "}
                  <strong>Individual Residential Property/ Small Individual Shop</strong>
                </Typography>
                <Typography
                  component="li"
                  sx={{
                    fontSize: "14px",
                    color: "#000000",
                    fontWeight: 500,
                    mb: 0.8,
                    lineHeight: 1.6,
                  }}
                >
                  Access – Private Properties/ Other Establishments (Other than
                  fuel stations) -{" "}
                  <strong>Agricultural Farms in Open Stretch</strong>
                </Typography>
                <Typography
                  component="li"
                  sx={{
                    fontSize: "14px",
                    color: "#000000",
                    fontWeight: 500,
                    lineHeight: 1.6,
                  }}
                >
                  ROW – Public Utility + OFC + Across the Highway + HDD Method
                </Typography>
              </Box>
            )}

            <Typography
              sx={{
                fontSize: "14px",
                color: "#000000",
                fontWeight: 500,
                lineHeight: 1.6,
              }}
            >
              The applicant may proceed with the submission of the application.
            </Typography>
          </Box>
        )}

        {!isNoPaymentCase && showProcessingSection && (
          <>
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 700,
                color: "#111827",
                mb: 1.5,
              }}
            >
              Processing Fee Details
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
                gap: 2,
              }}
            >
              {/* Transaction Date */}
              <StyledTextField
                type="date"
                label="Transaction Date *"
                size="small"
                sx={inputSX}
                value={processingFee.transactionDate}
                onChange={(e: any) => {
                  setProcessingFee((prev) => ({
                    ...prev,
                    transactionDate: e.target.value,
                  }));
                }}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: new Date().toISOString().split("T")[0] }}
                placeholder="Transaction Date"
              />

              {/* Amount Paid */}
              <StyledTextField
                type="number"
                inputProps={{ min: 0 }}
                label="Transaction Amount *"
                size="small"
                placeholder="Transaction Amount"
                sx={inputSX}
                value={processingFee.amountPaid}
                onChange={(e: any) => {
                  if (Number(e.target.value) < 0) return;
                  setProcessingFee((prev) => ({
                    ...prev,
                    amountPaid: e.target.value,
                  }));
                }}
              />

              {/* Transaction ID */}
              <StyledTextField
                label="Transaction ID *"
                size="small"
                placeholder="Transaction ID"
                sx={inputSX}
                value={processingFee.transactionId}
                onChange={(e: any) => {
                  setProcessingFee((prev) => ({
                    ...prev,
                    transactionId: sanitizeAlphaNumeric(e.target.value),
                  }));
                }}
              />
            </Box>

            {renderReceiptUpload(
              "legacy-processing-receipt",
              processingFee.receipt,
              "processing",
              (receipt) =>
                setProcessingFee((prev) => ({
                  ...prev,
                  receipt,
                })),
              () => setProcessingFee((prev) => ({ ...prev, receipt: null })),
            )}
          </>
        )}

        {!isNoPaymentCase && showProvisionalSection && (
          <>
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 700,
                color: "#111827",
                mt: 3,
                mb: 1.5,
              }}
            >
              Provisional NOC Fee Details
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
                gap: 2,
              }}
            >
              <StyledTextField
                type="date"
                label="Transaction Date *"
                size="small"
                sx={inputSX}
                value={provisionalFee.transactionDate}
                onChange={(e: any) => {
                  setProvisionalFee((prev) => ({
                    ...prev,
                    transactionDate: e.target.value,
                  }));
                }}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: new Date().toISOString().split("T")[0] }}
                placeholder="Transaction Date"
              />

              <StyledTextField
                type="number"
                inputProps={{ min: 0 }}
                label="Transaction Amount *"
                size="small"
                placeholder="Transaction Amount"
                sx={inputSX}
                value={provisionalFee.amountPaid}
                onChange={(e: any) => {
                  if (Number(e.target.value) < 0) return;
                  setProvisionalFee((prev) => ({
                    ...prev,
                    amountPaid: e.target.value,
                  }));
                }}
              />

              <StyledTextField
                label="Transaction ID *"
                size="small"
                placeholder="Transaction ID"
                sx={inputSX}
                value={provisionalFee.transactionId}
                onChange={(e: any) => {
                  setProvisionalFee((prev) => ({
                    ...prev,
                    transactionId: sanitizeAlphaNumeric(e.target.value),
                  }));
                }}
              />
            </Box>
            {renderReceiptUpload(
              "legacy-provisional-receipt",
              provisionalFee.receipt,
              "provisional",
              (receipt) =>
                setProvisionalFee((prev) => ({
                  ...prev,
                  receipt,
                })),
              () => setProvisionalFee((prev) => ({ ...prev, receipt: null })),
            )}
          </>
        )}

        {!isNoPaymentCase && showFinalSection && (
          <>
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 700,
                color: "#111827",
                mt: 3,
                mb: 1.5,
              }}
            >
              Final NOC Fee Details
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
                gap: 2,
              }}
            >
              <StyledTextField
                type="date"
                label="Transaction Date *"
                size="small"
                sx={inputSX}
                value={finalFee.transactionDate}
                onChange={(e: any) => {
                  setFinalFee((prev) => ({
                    ...prev,
                    transactionDate: e.target.value,
                  }));
                }}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: new Date().toISOString().split("T")[0] }}
                placeholder="Transaction Date"
              />

              <StyledTextField
                type="number"
                inputProps={{ min: 0 }}
                label="Transaction Amount *"
                size="small"
                placeholder="Transaction Amount"
                sx={inputSX}
                value={finalFee.amountPaid}
                onChange={(e: any) => {
                  if (Number(e.target.value) < 0) return;
                  setFinalFee((prev) => ({
                    ...prev,
                    amountPaid: e.target.value,
                  }));
                }}
              />

              <StyledTextField
                label="Transaction ID *"
                size="small"
                placeholder="Transaction ID"
                sx={inputSX}
                value={finalFee.transactionId}
                onChange={(e: any) => {
                  setFinalFee((prev) => ({
                    ...prev,
                    transactionId: sanitizeAlphaNumeric(e.target.value),
                  }));
                }}
              />
            </Box>

            {renderReceiptUpload(
              "legacy-final-receipt",
              finalFee.receipt,
              "final",
              (receipt) =>
                setFinalFee((prev) => ({
                  ...prev,
                  receipt,
                })),
              () => setFinalFee((prev) => ({ ...prev, receipt: null })),
            )}
          </>
        )}

        {!isNoPaymentCase && showBankGuaranteeSection && (
          <>
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 700,
                color: "#111827",
                mt: 3,
                mb: 1.5,
              }}
            >
              Bank Guarantee Details
              {/* {isAccessFinalWithOptionalBG && (
                <Typography
                  component="span"
                  sx={{
                    fontSize: "12px",
                    fontWeight: 400,
                    color: "#6b7280",
                    ml: 1,
                  }}
                >
                  (Optional)
                </Typography>
              )} */}
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" },
                gap: 2,
              }}
            >
              {/* ✅ NEW: Bank Name */}
              <StyledTextField
                label={
                  isAccessFinalWithOptionalBG ? "Bank Name" : "Bank Name *"
                }
                size="small"
                placeholder="Bank Name"
                sx={inputSX}
                value={bankGuarantee.bgBankName}
                onChange={(e: any) => {
                  setBankGuarantee((prev) => ({
                    ...prev,
                    bgBankName: e.target.value,
                  }));
                }}
              />
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label={
                    isAccessFinalWithOptionalBG
                      ? "Bank Guarantee Date of Submission"
                      : "Bank Guarantee Date of Submission *"
                  }
                  value={bgDateDayjs}
                  maxDate={dayjs()}
                  onChange={(newValue) => {
                    const nextBgDate =
                      newValue && newValue.isValid()
                        ? newValue.format("YYYY-MM-DD")
                        : "";

                    setBankGuarantee((prev) => ({
                      ...prev,
                      bgDate: nextBgDate,
                      bgExpiryDate:
                        !nextBgDate ||
                          (prev.bgExpiryDate &&
                            (prev.bgExpiryDate <= nextBgDate ||
                              prev.bgExpiryDate > getBGMaxExpiryDate(nextBgDate)))
                          ? ""
                          : prev.bgExpiryDate,
                      bgClaimDate:
                        !nextBgDate ||
                          (prev.bgClaimDate &&
                            (prev.bgClaimDate < nextBgDate ||
                              prev.bgClaimDate > getBGMaxExpiryDate(nextBgDate)))
                          ? ""
                          : prev.bgClaimDate,
                    }));
                  }}
                  format="DD/MM/YYYY"
                  slotProps={{
                    textField: {
                      size: "small",
                      sx: inputSX,
                      placeholder: "Bank Guarantee Date of Submission",
                      fullWidth: true,
                    },
                  }}
                />
              </LocalizationProvider>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label={
                    isAccessFinalWithOptionalBG
                      ? "Bank Guarantee Expiry Date"
                      : "Bank Guarantee Expiry Date *"
                  }
                  value={bgExpiryDateDayjs}
                  disabled={!bgDateDayjs}
                  minDate={bgDateDayjs ? bgDateDayjs.add(1, "day") : undefined}
                  maxDate={bgMaxExpiryDayjs || undefined}
                  onChange={(newValue) => {
                    setBankGuarantee((prev) => ({
                      ...prev,
                      bgExpiryDate:
                        newValue && newValue.isValid()
                          ? newValue.format("YYYY-MM-DD")
                          : "",
                    }));
                  }}
                  format="DD/MM/YYYY"
                  slotProps={{
                    textField: {
                      size: "small",
                      sx: inputSX,
                      placeholder: "Bank Guarantee Expiry Date",
                      fullWidth: true,
                    },
                  }}
                />
              </LocalizationProvider>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label={
                    isAccessFinalWithOptionalBG
                      ? "Bank Guarantee Claim Date"
                      : "Bank Guarantee Claim Date *"
                  }
                  value={bgClaimDateDayjs}
                  disabled={!bgDateDayjs}
                  minDate={bgDateDayjs || undefined}
                  maxDate={bgMaxExpiryDayjs || undefined}
                  onChange={(newValue) => {
                    setBankGuarantee((prev) => ({
                      ...prev,
                      bgClaimDate:
                        newValue && newValue.isValid()
                          ? newValue.format("YYYY-MM-DD")
                          : "",
                    }));
                  }}
                  format="DD/MM/YYYY"
                  slotProps={{
                    textField: {
                      size: "small",
                      sx: inputSX,
                      placeholder: "Bank Guarantee Claim Date",
                      fullWidth: true,
                    },
                  }}
                />
              </LocalizationProvider>
              <StyledTextField
                label={
                  isAccessFinalWithOptionalBG
                    ? "Bank Guarantee Number"
                    : "Bank Guarantee Number *"
                }
                size="small"
                placeholder="Bank Guarantee Number"
                sx={inputSX}
                value={bankGuarantee.bgNumber}
                onChange={(e: any) => {
                  setBankGuarantee((prev) => ({
                    ...prev,
                    bgNumber: sanitizeAlphaNumeric(e.target.value),
                  }));
                }}
              />

              <StyledTextField
                type="number"
                inputProps={{ min: 0 }}
                label={
                  isAccessFinalWithOptionalBG
                    ? "Bank Guarantee Amount"
                    : "Bank Guarantee Amount *"
                }
                size="small"
                placeholder="Bank Guarantee Amount"
                sx={inputSX}
                value={bankGuarantee.bgAmount}
                onChange={(e: any) => {
                  if (Number(e.target.value) < 0) return;
                  setBankGuarantee((prev) => ({
                    ...prev,
                    bgAmount: e.target.value,
                  }));
                }}
              />

              {/* ✅ NEW: Branch Address */}
              <StyledTextField
                label={
                  isAccessFinalWithOptionalBG ? "Branch Address" : "Branch Address *"
                }
                size="small"
                placeholder="Branch Address"
                sx={{ ...inputSX, gridColumn: "1 / -1" }}
                value={bankGuarantee.branchAddress}
                onChange={(e: any) => {
                  setBankGuarantee((prev) => ({
                    ...prev,
                    branchAddress: e.target.value,
                  }));
                }}
              />
            </Box>

            {renderReceiptUpload(
              "legacy-bg-receipt",
              bankGuarantee.receipt,
              "bg",
              (receipt) =>
                setBankGuarantee((prev) => ({
                  ...prev,
                  receipt,
                })),
              () => setBankGuarantee((prev) => ({ ...prev, receipt: null })),
              isAccessFinalWithOptionalBG,
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default LegacyPaymentDetail;
